/**
 * OntologySchemaFactory - Factory functions for creating OntologySchema instances.
 *
 * Provides convenient methods for creating OntologySchema instances from
 * Turtle content, automatically extracting ontology metadata (IRI, versionIRI, imports).
 * The full parsed Ontology is stored in OntologySchema for later use by taxonomy builders.
 */

import OntologySchema from '@semantical/modules/ontology-model/OntologySchema.ts';
import { parseTurtle, iriToString } from '@semantical/modules/owl2';
import { buildSchemaIndividualsFromOntology } from '@semantical/modules/ontology-model/taxonomyBuilders.ts';
import type { DomainConfig } from './domainRegistry.ts';

/**
 * Create an OntologySchema from Turtle content.
 *
 * Parses the Turtle content ONCE and stores the full Ontology object in the schema.
 * The Ontology object contains all axioms, declarations, and metadata from the TTL.
 * Taxonomy builders and other consumers should use the stored Ontology instead of re-parsing.
 *
 * @param ttl - The Turtle content to parse
 * @param name - The display name for the OntologySchema
 * @param fallbackNamespace - Optional namespace to use as ontologyIRI if not declared in Turtle
 * @param sourceUrl - Optional source URL(s) of the file(s) from which the TTL was loaded
 * @param prefixId - Optional short identifier to use as prefix (defaults to deriving from namespace)
 * @returns A new OntologySchema instance with the full parsed Ontology stored
 *
 * @example
 * ```typescript
 * const schema = createOntologySchemaFromTTL(turtleContent, 'Party Ontology', 'http://example.org/party#', undefined, 'party');
 * console.log(schema.ontologyIRI); // From Turtle or fallback
 * console.log(schema.ontology); // The full parsed Ontology object
 * ```
 */
export async function createOntologySchemaFromTTL(
    ttl: string,
    name: string,
    fallbackNamespace?: string,
    sourceUrl?: string,
    prefixId?: string
): Promise<OntologySchema> {
    // Parse TTL once and store the full Ontology
    const ontology = await parseTurtle(ttl);

    // Extract metadata from the parsed ontology
    const ontologyIRI = ontology.ontologyIRI
        ? iriToString(ontology.ontologyIRI)
        : (fallbackNamespace ?? name);

    const versionIRI = ontology.versionIRI
        ? iriToString(ontology.versionIRI)
        : undefined;

    const imports = ontology.imports.map(imp => iriToString(imp.iri));

    // Process prefixes: replace empty prefix ('') with a derived identifier
    // This handles cases like @prefix : <http://example.org/party#> .
    const processedPrefixes = ontology.prefixes.map(p => {
        const prefix = p.name;
        const namespace = p.iri;

        if (prefix === '') {
            // For empty prefixes, derive a short name from the namespace
            // e.g., "http://example.org/party#" -> "party"
            const match = namespace.match(/\/([^/#]+)[#/]?$/);
            if (match) {
                return { prefix: match[1].toLowerCase(), namespace };
            }
            // Fallback to prefixId if namespace derivation fails
            if (prefixId) {
                return { prefix: prefixId, namespace };
            }
        }
        return { prefix, namespace };
    });

    const schema = new OntologySchema(name, ontologyIRI, {
        versionIRI,
        imports,
        prefixes: processedPrefixes,
        ontology, // Store the full parsed Ontology
        sourceUrl
    });

    // Build and add SchemaIndividuals from the parsed ontology
    const namespace = fallbackNamespace ?? ontologyIRI;
    const schemaIndividuals = buildSchemaIndividualsFromOntology(ontology, namespace, [namespace]);
    for (const individual of schemaIndividuals.values()) {
        schema.addIndividual(individual);
    }

    return schema;
}

/**
 * Create an OntologySchema from a DomainConfig.
 *
 * Convenience method that uses the domain's TTL content and namespace
 * to create an OntologySchema.
 *
 * @param domain - The DomainConfig containing TTL content and namespace
 * @returns A new OntologySchema instance
 *
 * @example
 * ```typescript
 * const domain = getDomainById('archimate');
 * const schema = createOntologySchemaFromDomain(domain);
 * ```
 */
export async function createOntologySchemaFromDomain(domain: DomainConfig): Promise<OntologySchema> {
    const sourceUrl = domain.ttlFiles ? domain.ttlFiles.join(', ') : undefined;
    return createOntologySchemaFromTTL(domain.ttl, domain.label, domain.ns, sourceUrl, domain.id);
}

/**
 * Async version of createOntologySchemaFromDomain for use with Workspace.
 *
 * This function signature matches what Workspace.createWithAutoload expects
 * for its schemaFactory parameter.
 *
 * @param domain - Object containing at minimum id, label, ttl, and ns properties (ttlFiles optional for source tracking)
 * @returns Promise resolving to a new OntologySchema instance
 */
export async function createOntologySchemaFromDomainAsync(
    domain: { id: string; label: string; ttl: string; ns: string; ttlFiles?: string[] }
): Promise<OntologySchema> {
    const sourceUrl = domain.ttlFiles ? domain.ttlFiles.join(', ') : undefined;
    return await createOntologySchemaFromTTL(domain.ttl, domain.label, domain.ns, sourceUrl, domain.id);
}

/**
 * Create an OntologySchema from an already-parsed Ontology object.
 *
 * Used when loading from remote URLs where parsing has already occurred.
 * This function skips the parsing step and directly creates the schema.
 *
 * @param ontology - The already-parsed Ontology object
 * @param sourceUrl - The URL from which the ontology was loaded
 * @returns A new OntologySchema instance
 *
 * @example
 * ```typescript
 * const ontology = await parseRdfXml(rdfxmlContent);
 * const schema = createOntologySchemaFromOntology(ontology, 'https://example.org/ontology.rdf');
 * ```
 */
export function createOntologySchemaFromOntology(
    ontology: import('@semantical/modules/owl2/types/Ontology.ts').Ontology,
    sourceUrl: string
): OntologySchema {
    // Extract metadata from the parsed ontology
    const ontologyIRI = ontology.ontologyIRI
        ? iriToString(ontology.ontologyIRI)
        : sourceUrl;

    // Extract name from IRI (last path segment or hash fragment)
    const name = extractNameFromIRI(ontologyIRI);

    const versionIRI = ontology.versionIRI
        ? iriToString(ontology.versionIRI)
        : undefined;

    const imports = ontology.imports.map(imp => iriToString(imp.iri));

    // Process prefixes: replace empty prefix ('') with a derived name
    const processedPrefixes = ontology.prefixes.map(p => {
        const prefix = p.name;
        const namespace = p.iri;

        if (prefix === '') {
            // For empty prefixes, try to derive a name from the namespace
            const match = namespace.match(/\/([^/#]+)[#/]?$/);
            if (match) {
                return { prefix: match[1].toLowerCase(), namespace };
            }
        }
        return { prefix, namespace };
    });

    const schema = new OntologySchema(name, ontologyIRI, {
        versionIRI,
        imports,
        prefixes: processedPrefixes,
        ontology, // Store the full parsed Ontology
        sourceUrl
    });

    // Build and add SchemaIndividuals from the parsed ontology
    const namespace = ontologyIRI;
    const schemaIndividuals = buildSchemaIndividualsFromOntology(ontology, namespace, [namespace]);
    for (const individual of schemaIndividuals.values()) {
        schema.addIndividual(individual);
    }

    return schema;
}

/**
 * Extract a name from an IRI (last path segment or hash fragment).
 */
function extractNameFromIRI(iri: string): string {
    // Try to get the last path segment before # or /
    const hashIndex = iri.lastIndexOf('#');
    const slashIndex = iri.lastIndexOf('/');

    let basePart: string;
    if (hashIndex > slashIndex) {
        // IRI ends with #, get the segment before it
        basePart = iri.substring(0, hashIndex);
    } else {
        basePart = iri;
    }

    // Get the last path segment
    const lastSlash = basePart.lastIndexOf('/');
    if (lastSlash >= 0 && lastSlash < basePart.length - 1) {
        return basePart.substring(lastSlash + 1);
    }

    return iri;
}
